import { Accordion } from "radix-ui";
import useLocalStorageState from "use-local-storage-state";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import MinusIcon from "../icons/MinusIcon";
import PlusIcon from "../icons/PlusIcon";
import KursusDeltagereHandlingerDropdown, {
  KursusDeltagereHandlingerDropdownProps,
} from "../pages/kursus/id/Deltagere/KursusDeltagereHandlingerDropdown";
import { ReactNode, useEffect, useRef, useState } from "react";

export const AccordionRoot = (props: {
  children: React.ReactNode;
  className?: string;
  defaultValue?: string[];
  // type: "multiple" | "single";

  /**
   * If set, the open/close state of accordion items will be persisted in local storage.
   * Key must be unique for the application.
   */
  localstorageKey?: string;
}) => {
  const { children, className, localstorageKey, defaultValue } = props;
  if (localstorageKey) {
    return (
      <AccordionRootLocalStorage
        type="multiple"
        className={className}
        localstorageKey={localstorageKey}
      >
        {children}
      </AccordionRootLocalStorage>
    );
  }
  return (
    <Accordion.Root
      type="multiple"
      defaultValue={defaultValue}
      className={`c-accordion ${className ?? ""}`}
    >
      {children}
    </Accordion.Root>
  );
};
const AccordionRootLocalStorage = (props: {
  children: React.ReactNode;
  className?: string;
  type: "multiple" | "single";
  localstorageKey: string;
}) => {
  const { children, type, className, localstorageKey } = props;
  if (type === "multiple") {
    return (
      <AccordionRootMultipleLocalStorage
        className={className}
        localstorageKey={localstorageKey}
      >
        {children}
      </AccordionRootMultipleLocalStorage>
    );
  }
  return (
    <AccordionRootSingleLocalStorage
      className={className}
      localstorageKey={localstorageKey}
    >
      {children}
    </AccordionRootSingleLocalStorage>
  );
};
const AccordionRootMultipleLocalStorage = (props: {
  children: React.ReactNode;
  className?: string;
  localstorageKey: string;
}) => {
  const { children, className, localstorageKey } = props;
  const [value, setValue] = useLocalStorageState<string[]>(localstorageKey);
  return (
    <Accordion.Root
      type={"multiple"}
      className={`c-accordion ${className ?? ""}`}
      value={value}
      onValueChange={(value) => setValue(value)}
    >
      {children}
    </Accordion.Root>
  );
};
const AccordionRootSingleLocalStorage = (props: {
  children: React.ReactNode;
  className?: string;
  localstorageKey: string;
}) => {
  const { children, className, localstorageKey } = props;
  const [value, setValue] = useLocalStorageState<string>(localstorageKey);
  return (
    <Accordion.Root
      type={"single"}
      className={`c-accordion ${className ?? ""}`}
      value={value}
      onValueChange={(value) => setValue(value)}
    >
      {children}
    </Accordion.Root>
  );
};

export const AccordionItem = (props: {
  children: ReactNode;
  className?: string;
  value: string;
  isEmpty?: boolean;
  isDisabled?: boolean;
}) => {
  const { children, className, value, isEmpty, isDisabled } = props;
  let classes = className ?? "";
  if (isEmpty) classes += " is-empty";
  if (isDisabled) classes += " is-disabled";
  return (
    <Accordion.Item className={classes} value={value}>
      {children}
    </Accordion.Item>
  );
};

export const AccordionHeader = (props: {
  children: ReactNode;
  className?: string;
}) => {
  const { children, className } = props;
  return (
    <Accordion.Header className={`c-accordion-header ${className ?? ""}`}>
      {children}
    </Accordion.Header>
  );
};
export const AccordionTrigger = (props: {
  children: ReactNode;
  className?: string;
}) => {
  const { children, className } = props;
  return (
    <Accordion.Trigger className={`c-accordion-trigger ${className ?? ""}`}>
      {children}
      <PlusIcon width={20} />
      <MinusIcon width={20} />
      <span className="btn btn-secondary btn--icon">
        <ChevronDownIcon width={20} />
      </span>
    </Accordion.Trigger>
  );
};
const AccordionTriggerHandlinger = ({
  children,
  className,
  handlingerProps,
}: {
  children: ReactNode;
  handlingerProps: KursusDeltagereHandlingerDropdownProps;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const accordionTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const dataState = accordionTriggerRef.current?.getAttribute("data-state");
    setIsOpen(dataState === "open");
  }, [setIsOpen]);

  const handleClick = () => {
    if (accordionTriggerRef.current) {
      const currentState =
        accordionTriggerRef.current.getAttribute("data-state");
      const newState = currentState === "open" ? "closed" : "open";
      accordionTriggerRef.current.setAttribute("data-state", newState);
      setIsOpen((prevState) => !prevState);
    }
  };

  return (
    <div className="c-accordion-trigger flex-row justify-content-between">
      <Accordion.Trigger
        ref={accordionTriggerRef}
        className={`c-accordion-trigger ${className ?? ""}`}
        style={{ marginLeft: "-2rem" }}
        onClick={handleClick}
      >
        {children}
        <PlusIcon width={20} />
        <MinusIcon width={20} />
        <span className="btn btn-secondary btn--icon btn--sm">
          <ChevronDownIcon width={20} />
        </span>
      </Accordion.Trigger>
      {isOpen && <KursusDeltagereHandlingerDropdown {...handlingerProps} />}
    </div>
  );
};

export const AccordionContent = (props: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { children, className } = props;
  return (
    <Accordion.Content className={`c-accordion__content ${className ?? ""}`}>
      {children}
    </Accordion.Content>
  );
};

export const AccordionCard = ({
  title,
  localstorageKey,
  value,
  children,
  defaultValue,
  isEmpty,
  isDisabled,
  handlingerProps,
}: {
  title: string;
  value: string;
  children?: ReactNode;
  isEmpty?: boolean;
  isDisabled?: boolean;
  localstorageKey: string;
  defaultValue?: string[];
  handlingerProps?: KursusDeltagereHandlingerDropdownProps;
}) => {
  return (
    <AccordionRoot
      localstorageKey={localstorageKey}
      defaultValue={defaultValue}
    >
      <AccordionItem
        value={value}
        className="c-card c-card--accordion"
        isEmpty={isEmpty}
        isDisabled={isDisabled}
      >
        <AccordionHeader>
          {handlingerProps ? (
            <AccordionTriggerHandlinger
              handlingerProps={{ ...handlingerProps }}
            >
              {<>{title}</>}
            </AccordionTriggerHandlinger>
          ) : (
            <AccordionTrigger>{<>{title}</>}</AccordionTrigger>
          )}
        </AccordionHeader>
        <AccordionContent>{children}</AccordionContent>
      </AccordionItem>
    </AccordionRoot>
  );
};

export const AccordionFilterItem = (props: {
  title: string;
  value: string;
  children?: ReactNode;
  isActive?: boolean;
  localstorageKey?: string;
  defaultValue?: string[];
}) => {
  return (
    <AccordionRoot
      localstorageKey={props.localstorageKey}
      defaultValue={props.defaultValue}
      className="c-accordion c-accordion--filters"
    >
      <AccordionItem
        value={props.value}
        className={`c-accordion-item${props.isActive ? " is-active" : ""}`}
      >
        <AccordionHeader>
          <AccordionTrigger>
            <>
              {props.title}
              {props.isActive && (
                <span className="badge smaller rounded-pill ms-auto">
                  Aktivt filter
                </span>
              )}
            </>
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>{props.children}</AccordionContent>
      </AccordionItem>
    </AccordionRoot>
  );
};

export const AccordionSubFilterItem = (props: {
  title: string;
  value: string;
  children?: ReactNode;
  isActive?: boolean;
  localstorageKey?: string;
  defaultValue?: string[];
}) => {
  return (
    <AccordionRoot
      localstorageKey={props.localstorageKey}
      defaultValue={props.defaultValue}
      className="c-accordion c-accordion--filters mt-1 mb-1"
    >
      <AccordionItem
        value={props.value}
        className={`c-accordion-sub-item${props.isActive ? " is-active" : ""}`}
      >
        <AccordionHeader>
          <AccordionTrigger>
            <>
              {props.title}
              {props.isActive && (
                <span className="badge small rounded-pill ms-auto">
                  Aktivt filter
                </span>
              )}
            </>
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>{props.children}</AccordionContent>
      </AccordionItem>
    </AccordionRoot>
  );
};
